import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ConfirmationModalComponent,
  ConfirmationModalConfig,
  getProjectRoleHolderIds,
  KeyValueGroup,
  Partner,
  Project
} from '@app/shared';
import {ProjectUtilsService} from '../../services/project-utils.service';
import {CommunicationService, DocumentService, ProjectService, ScreenService} from '@app/core';
import {GlobalValuesService} from '../../../../core/services/global-values.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, forkJoin, of, Subject, takeUntil} from 'rxjs';
import {AuthService} from '../../../../core/services/auth.service';
import {environment} from '../../../../../environments/environment';

@Component({
  selector: 'app-project-detail',
  standalone: false,

  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.less'
})
export class ProjectDetailComponent implements OnInit, OnDestroy {

  // Exponer las referencias a los templates mediante ViewChild
  @ViewChild('mainTpl', {static: true}) mainTemplate!: TemplateRef<any>;
  @ViewChild('sideTpl', {static: true}) sideTemplate!: TemplateRef<any>;
  @ViewChild('confirmationModal') confirmationModal!: ConfirmationModalComponent;
  private pendingState?: string;

  project!: Project;

  partnerOptions: Partner[] = [];
  cecEmployeeList: Partner[] = [];
  clientsList: Partner[] = [];

  selectedTabIndex = 0;

  isMobile$;

  loading: boolean = true;

  private destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute, private router: Router, private ps: ProjectUtilsService,
              private screenService: ScreenService, private projectService: ProjectService,
              private communicationService: CommunicationService, private documentService: DocumentService,
              public globalValuesService: GlobalValuesService, public authService: AuthService) {

    this.isMobile$ = screenService.isMobile$;
    this.globalValuesService.isPartnerReady$.pipe(takeUntilDestroyed())
      .subscribe(value => {
        if(value){}
        this.partnerOptions = globalValuesService.partnerlist;
        this.cecEmployeeList= globalValuesService.cecEmployeeList;
        this.clientsList= globalValuesService.clientsList;
      })
  }

  /**
   * Une el id escalar histórico con la lista nueva y con los objetos que resuelva el
   * backend. Al guardar, el primero vuelve al escalar y el resto a la lista, así que
   * el valor real de un campo multivalor es siempre la unión de las tres fuentes.
   */
  private getCombinedIds(scalarId?: number, listIds?: number[], resolved?: { id: number }[]): number[] {
    const ids: Set<number> = new Set();

    if (scalarId) {
      ids.add(scalarId);
    }

    (listIds || []).forEach(id => ids.add(id));
    (resolved || []).forEach(entity => ids.add(entity.id));

    return Array.from(ids);
  }

  getCombinedPartnerIds(): number[] {
    if (!this.project) return [];
    return this.getCombinedIds(this.project.partnerId, this.project.partnerIds, this.project.partners);
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      if (params['openDocuments'] === 'true') {
        this.selectedTabIndex = 1;
        //remover parámetro después de usarlo
        this.router.navigate([], {
          queryParams: { openChat: null },
          queryParamsHandling: 'merge'
        });
      }
    });
    this.ps.getProjectAndServiceData().subscribe((value) => {
      if (value) {
        this.project = this.ps.selectedProject!;
        this.loading = false;
        if (!this.project.chat) {
          this.getDocumentsAndChat();
        } else {
          this.ps.currentDetailChat$.next(this.project.chat);
          this.ps.currentChatEntetyName$.next(this.project.name);
        }
      } else {
        this.router.navigate(['/projects']).then();
      }
    });
  }

  getDocumentsAndChat() {
    const request = {
      chat: this.communicationService.getChat({
        startRow: 0,
        endRow: 1,
        sortProperty: 'id',
        sortDirection: 'asc',
        filterProperty: 'id',
        filterValue: this.project.chatId.toString(),
      }),
      documents: this.documentService.getDocument({
        startRow: 0,
        endRow: 10000,
        sortDirection: 'asc',
        sortProperty: 'id',
        entityId: this.project.id,
        entityType: 'Project',
      }).pipe(
        catchError((error) => {
          console.warn('[ProjectDetail] No se pudieron cargar documentos del proyecto. Se continua sin documentos.', {
            projectId: this.project?.id,
            error,
          });
          return of({data: {rows: []}});
        })
      ),
    }

    forkJoin(request).subscribe({
      next: ({chat, documents}) => {
        // Transformación equivalente al map
        this.project = {
          ...this.project,
          documents: documents.data?.rows || [],
          chat: chat?.data?.rows?.[0],
        }

        this.ps.selectedProject = this.project;
        this.ps.currentDetailChat$.next(this.project.chat);
        this.ps.currentChatEntetyName$.next(this.project.name);
      },
      error: (e) => console.error(e),
    })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openStateChangeConfirmation(value: string): void {
    this.pendingState = value;
    const config: ConfirmationModalConfig = {
      title: 'Cambiar estado del proyecto',
      message: '¿Estás seguro que deseas cambiar el estado del proyecto?',
      confirmText: 'Cambiar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: false
    };
    this.confirmationModal.config = config;
    this.confirmationModal.open();
  }

  onStateChangeConfirmed(data: {textValue?: string, dateValue?: Date}): void {
    // Actualizar el estado del modal para mostrar loading
    this.confirmationModal.config.processing = true;
    if (this.pendingState) {
      this.setState(this.pendingState);
    }
  }

  setState(value: string): void {
    this.projectService.patchProject({id: this.project.id, status: value}).subscribe({
      next: () => {
        this.project.status = value;
        this.confirmationModal.config.processing = false;
        this.confirmationModal.forceClose();
      },
      error: (e) => {
        console.error(e);
        this.confirmationModal.config.processing = false;
        this.confirmationModal.forceClose();
      }
    })
  }

  setKeyValueData(): KeyValueGroup[] {
    return [
      {
        layout: 'normal',
        name: 'normal',
        items: [
          {
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            label: 'Ref. proyecto',
            noWrap: false,
            editable: false,
            required: false,
            type: 'text',
            value: this.project!.reference,
            path: 'reference'
          },
          {
            label: 'Cotización',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            editable: false,
            noWrap: false,
            required: false,
            type: 'btn',
            path: this.project.odooEntityId ? 'odooEntityId' : 'odooSaleOrderLineId',
            value: {
              label: this.project.odooEntityId ? this.project.odooEntityId.toString() : this.project?.odooSaleOrderLineId?.toString(),
              localPrefix: 'odoo-icon.svg',
              sufix: 'open_in_new',
              link: environment.odooBaseUrl + (this.project.odooEntityId?.toString() || this.project?.odooSaleOrderLineId?.toString() || this.project?.odooSaleOrderOptionId?.toString()),
              isOdoo: true,
            }
          },
          {
            label: 'Drive',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            editable: true,
            noWrap: false,
            required: false,
            type: 'btn',
            path: 'driveLink',
            value: {
              label: this.project.driveLink,
              localPrefix: 'google_drive-icon.svg',
              sufix: 'open_in_new',
              link: this.project.driveLink,
              isRawLink: true,
            }
          },
          {
            label: 'Cliente',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.clientsList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar-multiselect',
            path: 'partnerIds',
            value: {
              userIds: this.getCombinedPartnerIds()
            },
          },
          // Roles de miembro del proyecto (SOU-481). Todos admiten VARIOS titulares:
          // el primero se guarda en la columna escalar histórica y el resto en la lista.
          // "Subgerente Proyectos" (administrationAgentId) queda fuera de uso.
          {
            label: 'Ejecutivo Operaciones',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar-multiselect',
            path: 'operationExecutiveIds',
            value: {
              userIds: getProjectRoleHolderIds(this.project, 'OperationExecutive'),
            },
          },
          {
            label: 'Gerente Administración',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar-multiselect',
            path: 'deputyProjectManagerIds',
            value: {
              userIds: getProjectRoleHolderIds(this.project, 'DeputyProjectManager'),
            },
          },
          {
            label: 'Controller',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar-multiselect',
            path: 'controllerIds',
            value: {
              userIds: getProjectRoleHolderIds(this.project, 'Controller'),
            },
          },
          {
            label: 'Ejecutivo Comercial',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar-multiselect',
            path: 'commercialExecutiveIds',
            value: {
              userIds: getProjectRoleHolderIds(this.project, 'CommercialExecutive'),
            },
          },
          {
            label: 'Jefe de Operaciones',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar-multiselect',
            path: 'operationsManagerIds',
            value: {
              userIds: getProjectRoleHolderIds(this.project, 'OperationsManager'),
            },
          },
          {
            label: 'Total Planificado',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            noWrap: false,
            // Solo lectura: lo calcula el backend sumando los servicios activos.
            // Se edita desde el Total Planificado de cada servicio.
            editable: false,
            required: false,
            type: 'text',
            numberValue: true,
            integerOnly: true,
            minValue: 1,
            path: 'totalPlanned',
            value: (this.project!.totalPlanned ?? 0).toString(),
          },
          {
            label: 'Fecha inicio',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'date',
            path: 'startPlanDate',
            value: {
              startDate: this.project!.startPlanDate,
            }
          },
          {
            label: 'Fecha fin',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'date',
            path: 'endPlanDate',
            value: {
              startDate: this.project!.endPlanDate,
            }
          },
        ],
      }
    ];
  }
}
